import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Card, CardContent,
  CircularProgress, Alert, Button, Stack, LinearProgress, Divider,
  Chip, Avatar, IconButton, Tooltip, Pagination, TextField,
  InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Skeleton, Fade, Zoom, Collapse, Snackbar, useMediaQuery, useTheme
} from '@mui/material';
import {
  History, TrendingUp, EmojiEvents, Insights, CalendarToday,
  PhotoCamera, CheckCircle, Error, Search, FilterList,
  Timeline, Assessment, Star, Refresh, Download,
  Visibility, Share, DeleteOutline
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { getUserHistory } from '../api/user';

const HistoryPage = () => {
  // Responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  // --- STATE MANAGEMENT ---
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Pagination & Filtering States - Responsive defaults
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [itemsPerPage, setItemsPerPage] = useState(isMobile ? 6 : isTablet ? 9 : 12);

  // Snackbar state for share feedback
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Update items per page when screen size changes
  useEffect(() => {
    setItemsPerPage(isMobile ? 6 : isTablet ? 9 : 12);
    setPage(1); // Reset to first page when changing layout
  }, [isMobile, isTablet]);

  // --- DATA FETCHING ---
  const fetchHistory = async (showRefreshLoader = false) => {
    try {
      if (showRefreshLoader) setRefreshing(true);
      else setLoading(true);

      const response = await getUserHistory();
      setHistory(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setError('Failed to load classification history. Please try again.');
      setHistory([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  // --- FILTERING & SORTING LOGIC ---
  const filteredAndSortedHistory = useMemo(() => {
    let filtered = [...history];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.category?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.suggestion?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category?.name === categoryFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();

      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }

      if (dateFilter !== 'all') {
        filtered = filtered.filter(item =>
          new Date(item.classifiedAt) >= filterDate
        );
      }
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.classifiedAt) - new Date(a.classifiedAt);
        case 'oldest':
          return new Date(a.classifiedAt) - new Date(b.classifiedAt);
        case 'confidence':
          return (b.resultConfidence || 0) - (a.resultConfidence || 0);
        case 'category':
          return (a.category?.name || '').localeCompare(b.category?.name || '');
        default:
          return new Date(b.classifiedAt) - new Date(a.classifiedAt);
      }
    });

    return filtered;
  }, [history, searchTerm, categoryFilter, dateFilter, sortBy]);

  // --- PAGINATION ---
  const paginatedHistory = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return filteredAndSortedHistory.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedHistory, page, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedHistory.length / itemsPerPage);

  // --- STATISTICS CALCULATION ---
  const stats = useMemo(() => {
    if (!history.length) return null;

    const totalClassifications = history.length;
    const averageConfidence = (
      history.reduce((sum, item) => sum + (item.resultConfidence || 0), 0) / totalClassifications
    ).toFixed(1);

    const categoryStats = {};
    history.forEach(item => {
      const category = item.category?.name || 'Unknown';
      categoryStats[category] = (categoryStats[category] || 0) + 1;
    });

    const topCategory = Object.entries(categoryStats)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || 'None';

    const recentActivity = history.filter(item => {
      const itemDate = new Date(item.classifiedAt);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return itemDate >= weekAgo;
    }).length;

    return {
      totalClassifications,
      averageConfidence,
      topCategory,
      recentActivity,
      categoryStats
    };
  }, [history]);

  // --- UTILITY FUNCTIONS ---
  const getCategoryColor = (category) => {
    const colors = {
      'Recyclable': '#43a047',
      'Organic': '#66bb6a',
      'Hazardous': '#e53935',
      'General': '#546e7a',
      'Electronic': '#2196f3'
    };
    return colors[category] || '#546e7a';
  };

  const getCategoryGradient = (category) => {
    const gradients = {
      'Recyclable': 'linear-gradient(135deg, #43a047, #66bb6a)',
      'Organic': 'linear-gradient(135deg, #66bb6a, #81c784)',
      'Hazardous': 'linear-gradient(135deg, #e53935, #ef5350)',
      'General': 'linear-gradient(135deg, #546e7a, #607d8b)',
      'Electronic': 'linear-gradient(135deg, #2196f3, #42a5f5)'
    };
    return gradients[category] || 'linear-gradient(135deg, #546e7a, #607d8b)';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Recyclable': '♻️',
      'Organic': '🌱',
      'Hazardous': '⚠️',
      'General': '🗑️',
      'Electronic': '🔌'
    };
    return icons[category] || '📦';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return '#43a047';
    if (confidence >= 70) return '#ff9800';
    return '#f44336';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Share classification function
  const handleShare = async (item) => {
    try {
      const shareUrl = `${window.location.origin}/shared-classification/${item.shareToken}`;
      const shareText = `Check out my waste classification result on Sortify! 🌱\n\nCategory: ${item.category?.name || 'Unknown'}\nConfidence: ${Math.round(item.resultConfidence || 0)}%\n\n${shareUrl}`;

      if (navigator.share) {
        await navigator.share({
          title: 'My Waste Classification - Sortify',
          text: shareText,
          url: shareUrl
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareText);
        setSnackbarMessage('🔗 Share link copied to clipboard! Share your eco achievement!');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      setSnackbarMessage('❌ Failed to share. Please try again.');
      setSnackbarOpen(true);
    }
  };

  // --- LOADING STATES ---
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1, sm: 2, md: 3 }
      }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
          <Skeleton
            variant="text"
            width={{ xs: 250, sm: 300, md: 400 }}
            height={{ xs: 40, sm: 50, md: 60 }}
            sx={{ mx: 'auto', mb: { xs: 1, sm: 2 } }}
          />
          <Skeleton
            variant="text"
            width={{ xs: 150, sm: 200, md: 250 }}
            height={{ xs: 20, sm: 25, md: 30 }}
            sx={{ mx: 'auto' }}
          />
        </Box>

        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          {[...Array(4)].map((_, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Skeleton
                variant="rectangular"
                height={{ xs: 120, sm: 130, md: 140 }}
                sx={{ borderRadius: { xs: 2, sm: 2.5, md: 3 } }}
              />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {[...Array(isMobile ? 3 : isTablet ? 4 : 6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Skeleton
                variant="rectangular"
                height={{ xs: 240, sm: 260, md: 280 }}
                sx={{ borderRadius: { xs: 2, sm: 2.5, md: 3 } }}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{
      py: { xs: 2, sm: 3, md: 4 },
      px: { xs: 1, sm: 2, md: 3 }
    }}>
      {/* Header Section */}
      <Fade in timeout={800}>
        <Box sx={{
          textAlign: 'center',
          mb: { xs: 3, sm: 4, md: 6 },
          px: { xs: 1, sm: 2 }
        }}>
          <Typography
            variant={isMobile ? "h4" : isTablet ? "h3" : "h2"}
            fontWeight={800}
            sx={{
              background: 'linear-gradient(135deg, #43a047, #66bb6a)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: { xs: 1, sm: 1.5, md: 2 },
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' }
            }}
          >
            📊 Classification History
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "h6"}
            color="text.secondary"
            sx={{
              maxWidth: { xs: 300, sm: 500, md: 600 },
              mx: 'auto',
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
              lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 }
            }}
          >
            Track your eco-journey and see how you're making a difference in waste management
          </Typography>
        </Box>
      </Fade>

      {/* Statistics Cards */}
      {stats && (
        <Zoom in timeout={1000}>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ mb: { xs: 3, sm: 3.5, md: 4 } }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={isMobile ? 4 : 8} sx={{
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                color: 'white',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                minHeight: { xs: 120, sm: 140, md: 160 },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: { xs: 60, sm: 80, md: 100 },
                  height: { xs: 60, sm: 80, md: 100 },
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  transform: { xs: 'translate(20px, -20px)', md: 'translate(30px, -30px)' }
                }
              }}>
                <CardContent sx={{
                  position: 'relative',
                  zIndex: 1,
                  p: { xs: 1.5, sm: 2, md: 2.5 },
                  '&:last-child': { pb: { xs: 1.5, sm: 2, md: 2.5 } }
                }}>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "flex-start" : "center"}
                    justifyContent="space-between"
                    mb={{ xs: 1, sm: 1.5, md: 2 }}
                    spacing={isMobile ? 0.5 : 0}
                  >
                    <Typography
                      variant={isMobile ? "subtitle2" : "h6"}
                      fontWeight={600}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' } }}
                    >
                      Total Classifications
                    </Typography>
                    <Assessment sx={{
                      fontSize: { xs: 24, sm: 28, md: 32 },
                      opacity: 0.8,
                      alignSelf: isMobile ? 'flex-end' : 'center'
                    }} />
                  </Stack>
                  <Typography
                    variant={isMobile ? "h4" : "h3"}
                    fontWeight={800}
                    sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}
                  >
                    {stats.totalClassifications}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      mt: { xs: 0.5, sm: 0.75, md: 1 },
                      fontSize: { xs: '0.75rem', sm: '0.825rem', md: '0.875rem' }
                    }}
                  >
                    Keep up the great work! 🌍
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={isMobile ? 4 : 8} sx={{
                background: 'linear-gradient(135deg, #ff9800, #ffb74d)',
                color: 'white',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                minHeight: { xs: 120, sm: 140, md: 160 },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: { xs: 60, sm: 80, md: 100 },
                  height: { xs: 60, sm: 80, md: 100 },
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  transform: { xs: 'translate(20px, -20px)', md: 'translate(30px, -30px)' }
                }
              }}>
                <CardContent sx={{
                  position: 'relative',
                  zIndex: 1,
                  p: { xs: 1.5, sm: 2, md: 2.5 },
                  '&:last-child': { pb: { xs: 1.5, sm: 2, md: 2.5 } }
                }}>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "flex-start" : "center"}
                    justifyContent="space-between"
                    mb={{ xs: 1, sm: 1.5, md: 2 }}
                    spacing={isMobile ? 0.5 : 0}
                  >
                    <Typography
                      variant={isMobile ? "subtitle2" : "h6"}
                      fontWeight={600}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' } }}
                    >
                      Average Accuracy
                    </Typography>
                    <TrendingUp sx={{
                      fontSize: { xs: 24, sm: 28, md: 32 },
                      opacity: 0.8,
                      alignSelf: isMobile ? 'flex-end' : 'center'
                    }} />
                  </Stack>
                  <Typography
                    variant={isMobile ? "h4" : "h3"}
                    fontWeight={800}
                    sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}
                  >
                    {stats.averageConfidence}%
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      mt: { xs: 0.5, sm: 0.75, md: 1 },
                      fontSize: { xs: '0.75rem', sm: '0.825rem', md: '0.875rem' }
                    }}
                  >
                    Precision matters! 🎯
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={isMobile ? 4 : 8} sx={{
                background: 'linear-gradient(135deg, #2196f3, #64b5f6)',
                color: 'white',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                minHeight: { xs: 120, sm: 140, md: 160 },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: { xs: 60, sm: 80, md: 100 },
                  height: { xs: 60, sm: 80, md: 100 },
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  transform: { xs: 'translate(20px, -20px)', md: 'translate(30px, -30px)' }
                }
              }}>
                <CardContent sx={{
                  position: 'relative',
                  zIndex: 1,
                  p: { xs: 1.5, sm: 2, md: 2.5 },
                  '&:last-child': { pb: { xs: 1.5, sm: 2, md: 2.5 } }
                }}>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "flex-start" : "center"}
                    justifyContent="space-between"
                    mb={{ xs: 1, sm: 1.5, md: 2 }}
                    spacing={isMobile ? 0.5 : 0}
                  >
                    <Typography
                      variant={isMobile ? "subtitle2" : "h6"}
                      fontWeight={600}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' } }}
                    >
                      Top Category
                    </Typography>
                    <EmojiEvents sx={{
                      fontSize: { xs: 24, sm: 28, md: 32 },
                      opacity: 0.8,
                      alignSelf: isMobile ? 'flex-end' : 'center'
                    }} />
                  </Stack>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight={800}
                    sx={{
                      fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.8rem' },
                      lineHeight: 1.2
                    }}
                  >
                    {getCategoryIcon(stats.topCategory)} {stats.topCategory}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      mt: { xs: 0.5, sm: 0.75, md: 1 },
                      fontSize: { xs: '0.75rem', sm: '0.825rem', md: '0.875rem' }
                    }}
                  >
                    Your specialty! 🏆
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={isMobile ? 4 : 8} sx={{
                background: 'linear-gradient(135deg, #9c27b0, #ba68c8)',
                color: 'white',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                minHeight: { xs: 120, sm: 140, md: 160 },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: { xs: 60, sm: 80, md: 100 },
                  height: { xs: 60, sm: 80, md: 100 },
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  transform: { xs: 'translate(20px, -20px)', md: 'translate(30px, -30px)' }
                }
              }}>
                <CardContent sx={{
                  position: 'relative',
                  zIndex: 1,
                  p: { xs: 1.5, sm: 2, md: 2.5 },
                  '&:last-child': { pb: { xs: 1.5, sm: 2, md: 2.5 } }
                }}>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "flex-start" : "center"}
                    justifyContent="space-between"
                    mb={{ xs: 1, sm: 1.5, md: 2 }}
                    spacing={isMobile ? 0.5 : 0}
                  >
                    <Typography
                      variant={isMobile ? "subtitle2" : "h6"}
                      fontWeight={600}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' } }}
                    >
                      Recent Activity
                    </Typography>
                    <Timeline sx={{
                      fontSize: { xs: 24, sm: 28, md: 32 },
                      opacity: 0.8,
                      alignSelf: isMobile ? 'flex-end' : 'center'
                    }} />
                  </Stack>
                  <Typography
                    variant={isMobile ? "h4" : "h3"}
                    fontWeight={800}
                    sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}
                  >
                    {stats.recentActivity}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      mt: { xs: 0.5, sm: 0.75, md: 1 },
                      fontSize: { xs: '0.75rem', sm: '0.825rem', md: '0.875rem' }
                    }}
                  >
                    This week 📅
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Zoom>
      )}

      {/* Filters and Controls */}
      <Fade in timeout={1200}>
        <Paper elevation={isMobile ? 2 : 6} sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 3, sm: 3.5, md: 4 },
          borderRadius: { xs: 3, sm: 3.5, md: 4 }
        }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, sm: 2.5, md: 3 }}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              placeholder="Search classifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: 'auto', md: 250 },
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
              size={isMobile ? "small" : "medium"}
            />

            <Grid container spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    label="Category"
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {stats && Object.keys(stats.categoryStats).map(category => (
                      <MenuItem key={category} value={category}>
                        {getCategoryIcon(category)} {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date</InputLabel>
                  <Select
                    value={dateFilter}
                    label="Date"
                    onChange={(e) => setDateFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">This Week</MenuItem>
                    <MenuItem value="month">This Month</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    label="Sort By"
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <MenuItem value="newest">Newest</MenuItem>
                    <MenuItem value="oldest">Oldest</MenuItem>
                    <MenuItem value="confidence">Confidence</MenuItem>
                    <MenuItem value="category">Category</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Show</InputLabel>
                  <Select
                    value={itemsPerPage}
                    label="Show"
                    onChange={(e) => {
                      setItemsPerPage(e.target.value);
                      setPage(1);
                    }}
                  >
                    <MenuItem value={6}>6 per page</MenuItem>
                    <MenuItem value={12}>12 per page</MenuItem>
                    <MenuItem value={18}>18 per page</MenuItem>
                    <MenuItem value={24}>24 per page</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Desktop filters */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {stats && Object.keys(stats.categoryStats).map(category => (
                    <MenuItem key={category} value={category}>
                      {getCategoryIcon(category)} {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Date</InputLabel>
                <Select
                  value={dateFilter}
                  label="Date"
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="newest">Newest</MenuItem>
                  <MenuItem value="oldest">Oldest</MenuItem>
                  <MenuItem value="confidence">Confidence</MenuItem>
                  <MenuItem value="category">Category</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 100 }}>
                <InputLabel>Show</InputLabel>
                <Select
                  value={itemsPerPage}
                  label="Show"
                  onChange={(e) => {
                    setItemsPerPage(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value={5}>5 per page</MenuItem>
                  <MenuItem value={10}>10 per page</MenuItem>
                  <MenuItem value={12}>12 per page</MenuItem>
                  <MenuItem value={20}>20 per page</MenuItem>
                  <MenuItem value={50}>50 per page</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ ml: { xs: 0, md: 'auto' }, alignSelf: { xs: 'center', md: 'auto' } }}>
              <Tooltip title="Refresh History">
                <IconButton
                  onClick={() => fetchHistory(true)}
                  disabled={refreshing}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                    color: 'white',
                    '&:hover': { background: 'linear-gradient(135deg, #388e3c, #43a047)' }
                  }}
                >
                  {refreshing ?
                    <CircularProgress size={isMobile ? 16 : 20} color="inherit" /> :
                    <Refresh fontSize={isMobile ? "small" : "medium"} />
                  }
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>

          {filteredAndSortedHistory.length > 0 && (
            <Box sx={{
              mt: { xs: 2, sm: 2.5 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 2 }
            }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Showing {paginatedHistory.length} of {filteredAndSortedHistory.length} classifications
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {searchTerm && (
                  <Chip
                    label={`Search: "${searchTerm}"`}
                    onDelete={() => setSearchTerm('')}
                    size="small"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  />
                )}
                {categoryFilter !== 'all' && (
                  <Chip
                    label={`Category: ${categoryFilter}`}
                    onDelete={() => setCategoryFilter('all')}
                    size="small"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Fade>

      {/* Error State */}
      {error && (
        <Fade in>
          <Alert
            severity="error"
            sx={{
              mb: { xs: 3, sm: 3.5, md: 4 },
              borderRadius: { xs: 2, sm: 2.5, md: 3 },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
            action={
              <Button
                color="inherit"
                size={isMobile ? "small" : "medium"}
                onClick={() => fetchHistory()}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </Fade>
      )}

      {/* Empty State */}
      {!loading && !error && filteredAndSortedHistory.length === 0 && (
        <Fade in>
          <Paper elevation={isMobile ? 2 : 6} sx={{
            p: { xs: 4, sm: 6, md: 8 },
            textAlign: 'center',
            borderRadius: { xs: 3, sm: 3.5, md: 4 },
            background: 'linear-gradient(135deg, #f8fff8, #ffffff)'
          }}>
            <History sx={{
              fontSize: { xs: 60, sm: 70, md: 80 },
              color: '#43a047',
              mb: { xs: 2, sm: 2.5, md: 3 },
              opacity: 0.7
            }} />
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight={700}
              sx={{
                mb: { xs: 1.5, sm: 2 },
                color: '#43a047',
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
              }}
            >
              {searchTerm || categoryFilter !== 'all' || dateFilter !== 'all'
                ? 'No matching classifications found'
                : 'Start Your Eco Journey!'
              }
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: { xs: 3, sm: 3.5, md: 4 },
                maxWidth: { xs: 280, sm: 400, md: 500 },
                mx: 'auto',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                lineHeight: { xs: 1.4, sm: 1.5 }
              }}
            >
              {searchTerm || categoryFilter !== 'all' || dateFilter !== 'all'
                ? 'Try adjusting your filters to see more results.'
                : 'Upload your first waste image to begin tracking your environmental impact and earning points!'
              }
            </Typography>
            <Button
              component={Link}
              to="/classify"
              variant="contained"
              size={isMobile ? "medium" : "large"}
              startIcon={<PhotoCamera />}
              sx={{
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                px: { xs: 2.5, sm: 3, md: 4 },
                py: { xs: 1, sm: 1.25, md: 1.5 },
                borderRadius: { xs: 2, sm: 2.5, md: 3 },
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Start Classifying
            </Button>
          </Paper>
        </Fade>
      )}

      {/* Classification History Grid */}
      {!loading && !error && paginatedHistory.length > 0 && (
        <Fade in timeout={1400}>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {paginatedHistory.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Zoom in timeout={1000 + index * 100}>
                  <Card elevation={isMobile ? 3 : 6} sx={{
                    height: '100%',
                    borderRadius: { xs: 2.5, sm: 3, md: 4 },
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: isMobile ? 'none' : 'translateY(-8px)',
                      boxShadow: isMobile ? 3 : 12
                    }
                  }}>
                    {/* Category Header */}
                    <Box sx={{
                      background: getCategoryGradient(item.category?.name),
                      p: { xs: 1.5, sm: 2 },
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: { xs: 40, sm: 50, md: 60 },
                        height: { xs: 40, sm: 50, md: 60 },
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '50%',
                        transform: { xs: 'translate(15px, -15px)', md: 'translate(20px, -20px)' }
                      }
                    }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ position: 'relative', zIndex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                          <Typography
                            variant={isMobile ? "h6" : "h5"}
                            sx={{
                              fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' }
                            }}
                          >
                            {getCategoryIcon(item.category?.name)}
                          </Typography>
                          <Typography
                            variant={isMobile ? "subtitle1" : "h6"}
                            color="white"
                            fontWeight={700}
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' } }}
                          >
                            {item.category?.name || 'Unknown'}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${Math.round(item.resultConfidence || 0)}%`}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            background: 'rgba(255,255,255,0.9)',
                            color: getCategoryColor(item.category?.name),
                            fontWeight: 700,
                            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' }
                          }}
                        />
                      </Stack>
                    </Box>

                    <CardContent sx={{
                      p: { xs: 2, sm: 2.5, md: 3 },
                      height: 'calc(100% - 80px)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      {/* Confidence Bar */}
                      <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.825rem', md: '0.875rem' } }}
                          >
                            Confidence
                          </Typography>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{
                              color: getConfidenceColor(item.resultConfidence),
                              fontSize: { xs: '0.7rem', sm: '0.75rem' }
                            }}
                          >
                            {item.resultConfidence >= 90 ? 'Excellent' :
                             item.resultConfidence >= 70 ? 'Good' : 'Fair'}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={item.resultConfidence || 0}
                          sx={{
                            height: { xs: 6, sm: 8 },
                            borderRadius: { xs: 3, sm: 4 },
                            backgroundColor: alpha(getCategoryColor(item.category?.name), 0.2),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getCategoryColor(item.category?.name),
                              borderRadius: { xs: 3, sm: 4 }
                            }
                          }}
                        />
                      </Box>

                      {/* Suggestion */}
                      <Box sx={{ flex: 1, mb: { xs: 2, sm: 2.5 } }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            mb: 1,
                            color: getCategoryColor(item.category?.name),
                            fontSize: { xs: '0.75rem', sm: '0.825rem', md: '0.875rem' }
                          }}
                        >
                          💡 Suggestion
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{
                          fontSize: { xs: '0.75rem', sm: '0.825rem', md: '0.875rem' },
                          lineHeight: { xs: 1.4, sm: 1.5 },
                          display: '-webkit-box',
                          WebkitLineClamp: isMobile ? 3 : 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: { xs: '60px', sm: '50px', md: '40px' }
                        }}>
                          {item.suggestion || 'No suggestion available'}
                        </Typography>
                      </Box>

                      <Box>
                        <Divider sx={{
                          my: { xs: 1.5, sm: 2 },
                          background: getCategoryGradient(item.category?.name),
                          height: { xs: 1.5, sm: 2 },
                          borderRadius: 1
                        }} />

                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarToday sx={{
                              fontSize: { xs: 14, sm: 16 },
                              color: getCategoryColor(item.category?.name),
                              opacity: 0.8
                            }} />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontWeight: 500,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                              }}
                            >
                              {formatDate(item.classifiedAt)}
                            </Typography>
                          </Stack>

                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                component={Link}
                                to={`/classification/${item.id}`}
                                sx={{
                                  color: getCategoryColor(item.category?.name),
                                  background: alpha(getCategoryColor(item.category?.name), 0.1),
                                  p: { xs: 0.5, sm: 0.75 },
                                  '&:hover': {
                                    background: alpha(getCategoryColor(item.category?.name), 0.2),
                                    transform: isMobile ? 'none' : 'scale(1.1)'
                                  }
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Share Classification">
                              <IconButton
                                size="small"
                                sx={{
                                  color: '#ff9800',
                                  background: alpha('#ff9800', 0.1),
                                  p: { xs: 0.5, sm: 0.75 },
                                  '&:hover': {
                                    background: alpha('#ff9800', 0.2),
                                    transform: isMobile ? 'none' : 'scale(1.1)'
                                  }
                                }}
                                onClick={() => handleShare(item)}
                              >
                                <Share fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Fade>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Fade in timeout={1600}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: { xs: 3, sm: 3.5, md: 4 },
            px: { xs: 1, sm: 0 }
          }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, newPage) => setPage(newPage)}
              color="primary"
              size={isMobile ? "small" : isTablet ? "medium" : "large"}
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={isMobile ? 1 : 2}
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: { xs: 1.5, sm: 2 },
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
                },
                '& .Mui-selected': {
                  background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                  color: 'white'
                }
              }}
            />
          </Box>
        </Fade>
      )}

      {/* Quick Actions */}
      <Fade in timeout={1800}>
        <Paper elevation={isMobile ? 2 : 6} sx={{
          p: { xs: 3, sm: 3.5, md: 4 },
          mt: { xs: 4, sm: 5, md: 6 },
          borderRadius: { xs: 3, sm: 3.5, md: 4 },
          background: 'linear-gradient(135deg, #f8fff8, #ffffff)',
          border: '2px solid #e8f5e8'
        }}>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight={700}
            sx={{
              mb: { xs: 2, sm: 2.5, md: 3 },
              color: '#43a047',
              textAlign: 'center',
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' }
            }}
          >
            🚀 Ready for More?
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.5, sm: 2 }}
            justifyContent="center"
          >
            <Button
              component={Link}
              to="/classify"
              variant="contained"
              size={isMobile ? "medium" : "large"}
              startIcon={<PhotoCamera />}
              sx={{
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                px: { xs: 2.5, sm: 3 },
                py: { xs: 1.25, sm: 1.5 },
                borderRadius: { xs: 2.5, sm: 3 },
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Classify More Waste
            </Button>
            <Button
              component={Link}
              to="/rewards"
              variant="outlined"
              size={isMobile ? "medium" : "large"}
              startIcon={<EmojiEvents />}
              sx={{
                borderColor: '#43a047',
                color: '#43a047',
                px: { xs: 2.5, sm: 3 },
                py: { xs: 1.25, sm: 1.5 },
                borderRadius: { xs: 2.5, sm: 3 },
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&:hover': {
                  background: alpha('#43a047', 0.1),
                  borderColor: '#43a047'
                }
              }}
            >
              View Rewards
            </Button>
            <Button
              component={Link}
              to="/leaderboard"
              variant="outlined"
              size={isMobile ? "medium" : "large"}
              startIcon={<Star />}
              sx={{
                borderColor: '#ff9800',
                color: '#ff9800',
                px: { xs: 2.5, sm: 3 },
                py: { xs: 1.25, sm: 1.5 },
                borderRadius: { xs: 2.5, sm: 3 },
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&:hover': {
                  background: alpha('#ff9800', 0.1),
                  borderColor: '#ff9800'
                }
              }}
            >
              Check Leaderboard
            </Button>
          </Stack>
        </Paper>
      </Fade>

      {/* Snackbar for share feedback */}
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            bgcolor: '#43a047',
            color: 'white',
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }
        }}
      />
    </Container>
  );
};

export default HistoryPage;
